import {useEffect, useState} from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Address } from "api/Community/CommunityInterfaces";

import GenericTable, {Column} from "components/Data/DataDisplays/GenericTable";
import ControlPanel from "components/Flow/Tabs/ItemMovementTabs/ControlPanel";

// 🔥 REQUIRED APIs for Add/Remove Addresses Buttons
import { addressesAPI } from "api/Community/AddressesAPI";
import MoveConfirmDialog from "./ItemMovementTabs/MoveConfirmDialog";

interface AddressesMovementTabProps {
  nodeData: any | null;
  nameOfFunctionalityField?: string;
  nodeAPI: any;
  getNodeAddresses: (nodeId: number) => Promise<Address[]>;
  moveAddressesBetweenNodes: (payload: any) => Promise<any>;

  // 🔥 Dynamic selectors
  inputTypeField?: string;
  outputTypeField?: string;
  inputLabelField?: string;
  outputLabelField?: string;

  // 🔥 Filter selectors
  inputFilterField?: string;
  outputFilterField?: string;

  includeMovementControls?: boolean;
}

export default function AddressesMovementTab({
  nodeData,
  nameOfFunctionalityField = "functionalities",
  nodeAPI,
  getNodeAddresses,
  moveAddressesBetweenNodes,

  inputTypeField = "inputEdgeType",
  outputTypeField = "outputEdgeType",
  inputLabelField = "inputLabel",
  outputLabelField = "outputLabel",

  inputFilterField = "inputEdgeType",
  outputFilterField = "outputEdgeType",
  includeMovementControls = true,
}: AddressesMovementTabProps) {
  const navigate = useNavigate();

  const [addresses, setAddresses] = useState<Address[]>([]);
  const [selectedAddresses, setSelectedAddresses] = useState<Address[]>([]);
  const [edges, setEdges] = useState<any[]>([]);
  const [selectedInputID, setSelectedInput] = useState<string>("");
  const [selectedOutputID, setSelectedOutput] = useState<string>("");
  // disable controls while moving
  const [moving, setMoving] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDirection, setPendingDirection] =
    useState<"forward" | "backward" | null>(null);

  useEffect(() => {
      if (!nodeData?.id) return;
      refreshAddresses();
    }, [nodeData?.id]);

  // ---------------------------------------
  // LOAD INITIAL NODE PEOPLE
  // ---------------------------------------

  if (!nodeData) {
    return <Typography>Select a node to edit</Typography>;
  }

  const functionalities = nodeData?.data[nameOfFunctionalityField] || [];

  const inputFunctionalities = functionalities.filter(
    (f: any) => f[inputFilterField] !== null && f[inputFilterField] !== undefined && f["inputEdgeType"] == 3 //addresses
  );

  const outputFunctionalities = functionalities.filter(
    (f: any) => f[outputFilterField] !== null && f[outputFilterField] !== undefined && f["outputEdgeType"] == 3 //addresses
  );

  // ---------------------------------------
  // REFRESH PEOPLE AFTER ANY CHANGE
  // ---------------------------------------
  const refreshAddresses = async () => {
    const refreshed = await getNodeAddresses(nodeData.id);
    setAddresses(refreshed);
    setSelectedAddresses([]);
  };

  // ---------------------------------------
  // MOVE PEOPLE (SAFE + AWAITED)
  // ---------------------------------------
  const handleMove = (direction: "forward" | "backward") => {
    if (moving) return;
    if (selectedAddresses.length === 0) return;

    const targetID =
      direction === "forward" ? selectedOutputID : selectedInputID;

    if (!targetID) return;

    setPendingDirection(direction);
    setConfirmOpen(true);
  };

  const confirmMove = async () => {
    if (!pendingDirection) return;

    setConfirmOpen(false);
    setMoving(true);

    try {
      const targetID =
        pendingDirection === "forward"
          ? selectedOutputID
          : selectedInputID;

      await moveAddressesBetweenNodes({
        node_id: nodeData.id,
        handle_name: targetID,
        direction: pendingDirection,
        item_field_name: "addresses",
        selected_items: selectedAddresses,
      });

      await refreshAddresses();
    } catch (error) {
      console.error("Failed to move addresses:", error);
    } finally {
      setMoving(false);
      setPendingDirection(null);
    }
  };

  const cancelMove = () => {
    setConfirmOpen(false);
    setPendingDirection(null);
  };


  const columns: Column<any>[] = [
    { field: "street_address", label: "Street Address", type: "text" },
    { field: "city", label: "City", type: "text" },
    { field: "post_code", label: "Postcode", type: "text" },
    { field: "country", label: "Country", type: "text" },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* ================= CONTROL PANEL ================= */}
      {includeMovementControls && (
        <ControlPanel
          inputFunctionalities={inputFunctionalities}
          outputFunctionalities={outputFunctionalities}

          selectedInputID={selectedInputID}
          selectedOutputID={selectedOutputID}
          setSelectedInput={setSelectedInput}
          setSelectedOutput={setSelectedOutput}

          handleMove={handleMove}
          disabled={moving}

          nodeData={nodeData}
          items={addresses}
          itemType="Addresses"
          selectedItems={selectedAddresses}
          refreshItems={refreshAddresses}
          nodeAPI={nodeAPI}
          itemAPI={addressesAPI}

          inputTypeField={inputTypeField}
          outputTypeField={outputTypeField}
          inputLabelField={inputLabelField}
          outputLabelField={outputLabelField}
        />
      )}

      {/* ================= PEOPLE TABLE ================= */}
      <GenericTable
        data={addresses}
        columns={columns}
        selectable={includeMovementControls && !moving}
        onSelectionChange={(rows) => setSelectedAddresses(rows)}
        linkHandler={() => navigate("/community/addresses/")}
        includePaper={false}
      />
      <MoveConfirmDialog
        open={confirmOpen}
        onConfirm={confirmMove}
        onCancel={cancelMove}
      />

    </Box>
  );
}
